import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';

// Accessible color palette for dark backgrounds
const PALETTE = [
  '#14b8a6', // teal-500
  '#6366f1', // indigo-500
  '#f59e0b', // amber-500
  '#ec4899', // pink-500
  '#10b981', // emerald-500
  '#8b5cf6', // violet-500
  '#f97316', // orange-500
  '#06b6d4', // cyan-500
  '#84cc16', // lime-500
  '#e879f9', // fuchsia-500
];

function buildDonutSlices(data, outerR, innerR, gap = 0.02) {
  const total = data.reduce((sum, d) => sum + d.total, 0);
  if (total === 0) return [];
  const slices = [];
  let startAngle = -Math.PI / 2;

  data.forEach((d, i) => {
    const fraction = d.total / total;
    const sweep = fraction * (2 * Math.PI) - gap;
    const endAngle = startAngle + sweep;

    const cos = (a) => Math.cos(a);
    const sin = (a) => Math.sin(a);
    const largeArc = sweep > Math.PI ? 1 : 0;

    const path = [
      `M ${cos(startAngle) * outerR} ${sin(startAngle) * outerR}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${cos(endAngle) * outerR} ${sin(endAngle) * outerR}`,
      `L ${cos(endAngle) * innerR} ${sin(endAngle) * innerR}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${cos(startAngle) * innerR} ${sin(startAngle) * innerR}`,
      'Z',
    ].join(' ');

    slices.push({
      path,
      color: PALETTE[i % PALETTE.length],
      fraction,
      category: d.category,
      correct: d.correct,
      total: d.total,
      pct: d.total > 0 ? Math.round((d.correct / d.total) * 100) : 0,
    });

    startAngle += fraction * 2 * Math.PI;
  });

  return slices;
}

/**
 * CategoryChart — SVG donut chart (no recharts).
 * Props: data [{category, correct, total}], title
 */
export default function CategoryChart({ data = [], title = 'Category Breakdown' }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const validData = data.filter((d) => d.total > 0);
  const totalQuestions = validData.reduce((s, d) => s + d.total, 0);
  const totalCorrect = validData.reduce((s, d) => s + d.correct, 0);
  const overallPct = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

  const outerR = 80;
  const innerR = 52;
  const viewSize = outerR * 2 + 10;
  const cx = outerR + 5;
  const cy = outerR + 5;

  const slices = useMemo(() => buildDonutSlices(validData, outerR, innerR), [validData]);

  if (validData.length === 0) {
    return (
      <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 flex items-center justify-center min-h-40">
        <p className="text-slate-500 text-sm">No category data available</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
      {title && (
        <h3 className="text-sm font-semibold text-slate-300 mb-4 uppercase tracking-wider">{title}</h3>
      )}
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* SVG Donut */}
        <div className="shrink-0">
          <svg
            viewBox={`0 0 ${viewSize} ${viewSize}`}
            width={viewSize}
            height={viewSize}
            style={{ maxWidth: 200 }}
            className="overflow-visible"
          >
            <g transform={`translate(${cx}, ${cy})`}>
              {slices.map((slice, i) => {
                const isHovered = hoveredIndex === i;
                return (
                  <motion.path
                    key={i}
                    d={slice.path}
                    fill={slice.color}
                    animate={{
                      opacity: hoveredIndex !== null && !isHovered ? 0.45 : 1,
                      scale: isHovered ? 1.05 : 1,
                    }}
                    style={{ transformOrigin: '0 0', filter: isHovered ? `drop-shadow(0 0 8px ${slice.color}88)` : 'none' }}
                    transition={{ duration: 0.15 }}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    tabIndex={0}
                    role="img"
                    aria-label={`${slice.category}: ${slice.correct}/${slice.total} (${slice.pct}%)`}
                    className="cursor-pointer focus:outline-none"
                  />
                );
              })}
            </g>
            {/* Center text */}
            <text x={cx} y={cy - 7} textAnchor="middle" dominantBaseline="middle"
              style={{ fontSize: 20, fontWeight: 700, fill: '#f1f5f9', fontFamily: 'inherit' }}>
              {hoveredIndex !== null ? `${slices[hoveredIndex].pct}%` : `${overallPct}%`}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" dominantBaseline="middle"
              style={{ fontSize: 9, fill: '#94a3b8', fontFamily: 'inherit' }}>
              {hoveredIndex !== null
                ? slices[hoveredIndex].category.slice(0, 12)
                : 'Overall'}
            </text>
          </svg>
        </div>

        {/* Legend */}
        <div className="flex-1 min-w-0 space-y-2 w-full">
          {slices.map((slice, i) => (
            <motion.div
              key={i}
              animate={{ opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.45 : 1 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-2 cursor-default"
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: slice.color }} />
              <span className="text-xs text-slate-300 flex-1 truncate" title={slice.category}>
                {slice.category}
              </span>
              <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden shrink-0">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: slice.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${slice.pct}%` }}
                  transition={{ duration: 0.9, delay: i * 0.07, ease: 'easeOut' }}
                />
              </div>
              <span className="text-xs text-slate-400 font-mono w-8 text-right shrink-0">{slice.pct}%</span>
              <span className="text-xs text-slate-600 font-mono w-12 text-right shrink-0 hidden sm:block">
                {slice.correct}/{slice.total}
              </span>
            </motion.div>
          ))}
          <div className="pt-2 mt-2 border-t border-slate-700 flex items-center justify-between">
            <span className="text-xs text-slate-400 font-semibold">Total</span>
            <span className="text-xs text-teal-400 font-semibold font-mono">
              {totalCorrect}/{totalQuestions} ({overallPct}%)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
